import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ArrowRight,
  MessageSquare,
  Truck,
  Palette,
  Ruler,
  Sparkles,
  Tag,
  Layers,
} from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { BUSINESS_NAME, IMG_PLAIN_TSHIRT, waLink } from "../lib/site";

const FABRICS = [
  { name: "Polyester Sarina", gsm: "110 GSM", price: 70, best: ["Promotions", "Events", "Budget Bulk Orders"] },
  { name: "Sports Dri-Fit", gsm: "Performance", price: 130, best: ["Sports Teams", "Gym Wear", "Running & Activewear"] },
  { name: "Cotton PC", gsm: "160 GSM", price: 175, best: ["Everyday Wear", "Online Selling", "Brand Launches"] },
  { name: "Cotton PC", gsm: "180 GSM", price: 195, best: ["Premium Feel", "Streetwear", "Meesho & Flipkart Sellers"] },
  { name: "China Cotton", gsm: "220 GSM", price: 210, best: ["Affordable Oversized", "Clothing Brands", "High Margin Products"] },
  { name: "French Terry", gsm: "240 GSM", price: 260, best: ["Premium Oversized", "Premium Clothing Brands"] },
];

const DIFFERENTIATORS = [
  { icon: Layers, title: "13 mm Rib Covering Tape", desc: "A clean, premium finish on the neckline for a high-end look." },
  { icon: Layers, title: "Double Fabric D-Back (French Terry)", desc: "Extra reinforced back panel for a structured, premium drape." },
  { icon: Ruler, title: "22 mm Rib", desc: "Durable, well-proportioned ribbing that holds shape wash after wash." },
  { icon: CheckCircle2, title: "Quality Stitching", desc: "Consistent, strong seams built for retail-grade durability." },
  { icon: Ruler, title: "Size Chart Customisation", desc: "Need a specific fit? We can customise the size chart for your brand." },
];

const BADGES = [
  { icon: Palette, label: "Multiple Colours Available" },
  { icon: Ruler, label: "Sizes: S, M, L, XL, XXL, 3XL" },
  { icon: Tag, label: "MOQ: 100 PCS" },
  { icon: Truck, label: "Fast Dispatch Across India" },
];

export default function TshirtManufacturing() {
  const waMsg = `Hi ${BUSINESS_NAME}, I'm interested in bulk plain t-shirt manufacturing (MOQ 100 pcs). Please share details & pricing.`;

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-slate-900 selection:text-white">
      <SiteHeader active="/premiumtshirtmanufacturing" />

      {/* Hero */}
      <section className="relative py-16 sm:py-20 border-b border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 border border-slate-200 text-xs font-mono tracking-wider uppercase text-slate-700">
                <Sparkles className="w-3.5 h-3.5 text-slate-900" /> Plain T-Shirt Wholesale Catalog
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.2] uppercase">
                Premium Solid <span className="bg-[#FFD400] text-slate-950 px-2 box-decoration-clone">T-Shirts</span> In Bulk
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-2xl font-medium">
                Retail-grade plain t-shirts across multiple fabrics & GSMs — built for clothing brands, online sellers and bulk buyers. Customisation possible.
              </p>
              <div className="grid grid-cols-2 gap-3 max-w-xl">
                {BADGES.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.label} className="flex items-center gap-2 border-2 border-slate-950 bg-slate-50 px-3 py-2.5">
                      <Icon className="w-4 h-4 text-slate-950 shrink-0" />
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-800">{b.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={waLink(waMsg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tshirt-wa-cta"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white hover:bg-[#20bd5a] text-sm font-bold uppercase tracking-wider px-8 py-4 transition-all duration-150 border border-slate-950 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] hover:translate-y-[-1px]"
                >
                  <MessageSquare className="w-4 h-4 fill-white" /> Order on WhatsApp
                </a>
                <a
                  href={waLink(`Hi ${BUSINESS_NAME}, I'd like to order a SAMPLE plain t-shirt before placing a bulk order. Please share details.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tshirt-sample-wa-cta"
                  className="inline-flex items-center gap-2 bg-white text-slate-950 hover:bg-slate-50 text-sm font-bold uppercase tracking-wider px-8 py-4 transition-all duration-150 border-2 border-slate-950 shadow-[4px_4px_0px_0px_rgba(255,212,0,0.8)] hover:translate-y-[-1px]"
                >
                  <MessageSquare className="w-4 h-4 text-[#25D366]" /> Order a Sample
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="border-2 border-slate-950 p-2 sm:p-3 bg-white shadow-[8px_8px_0px_0px_rgba(15,23,42,1)]">
                <img src={IMG_PLAIN_TSHIRT} alt="Premium solid t-shirts manufacturing" className="w-full h-auto object-cover border border-slate-200" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fabric Catalog */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase">Fabric, GSM & Pricing</h2>
            <p className="text-slate-600 font-medium">All prices are per piece. Minimum order quantity is <strong className="text-slate-950">100 pcs</strong>. Available in multiple colours with fast dispatch.</p>
          </div>

          <div data-testid="fabric-catalog" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FABRICS.map((f) => (
              <div key={f.name + f.gsm} className="bg-white border-2 border-slate-950 p-6 flex flex-col hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] transition-all">
                <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-4">
                  <div>
                    <h3 className="text-lg font-extrabold uppercase tracking-tight leading-tight">{f.name}</h3>
                    <span className="text-xs font-mono text-slate-500 uppercase">{f.gsm}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="block text-2xl font-extrabold font-mono text-slate-950">₹{f.price}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">/ piece</span>
                  </div>
                </div>
                <div className="pt-4 space-y-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Best For</p>
                  {f.best.map((b) => (
                    <p key={b} className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-slate-950 shrink-0" /> {b}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight uppercase">What Makes Our T-Shirts Better</h2>
            <p className="text-slate-600 font-medium">The premium details that set our solid t-shirts apart from ordinary wholesale t-shirts.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {DIFFERENTIATORS.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.title} className="border-2 border-slate-950 bg-slate-50 p-6 space-y-3">
                  <div className="w-11 h-11 bg-slate-950 text-white flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold uppercase text-slate-950 leading-tight">{d.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed font-medium">{d.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DTF Combo Offer */}
      <section className="py-16 bg-slate-950 text-white border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-5">
              <span className="inline-flex items-center gap-2 bg-[#25D366] text-slate-950 text-xs font-extrabold uppercase tracking-widest px-3 py-1.5">
                <Tag className="w-4 h-4" /> Limited Combo Offer
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold uppercase leading-tight">
                Get <span className="text-[#FFD400]">20% OFF on DTF</span> + <span className="text-[#FFD400]">FREE Designing</span> with your T-shirt orders
              </h2>
              <p className="text-slate-300 font-medium max-w-2xl leading-relaxed">
                Order plain t-shirts with us and unlock a flat 20% discount on DTF gang sheet printing — plus free design assistance from our team. Build a ready-to-sell product in one place.
              </p>

              {/* Quick DTF details */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 max-w-2xl">
                {["80+ Wash Guarantee", "Vibrant True Colours", "Soft Hand-Feel", "Easy To Peel", "100+ Ready Designs", '24" Wide Sheets'].map((t) => (
                  <div key={t} className="flex items-center gap-2 bg-slate-900 border border-slate-800 px-3 py-2">
                    <CheckCircle2 className="w-4 h-4 text-[#25D366] shrink-0" />
                    <span className="text-xs font-bold uppercase tracking-wide">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link
                to="/dtf"
                data-testid="tshirt-dtf-link"
                className="inline-flex items-center justify-center gap-2 bg-white text-slate-950 hover:bg-slate-100 text-sm font-bold uppercase tracking-wider px-6 py-4 border border-white transition-all"
              >
                <Layers className="w-4 h-4" /> Explore DTF Printing <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={waLink(`Hi ${BUSINESS_NAME}, I want to order T-shirts + DTF and claim the 20% off + free designing combo.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white hover:bg-[#20bd5a] text-sm font-bold uppercase tracking-wider px-6 py-4 border border-slate-950 transition-all"
              >
                <MessageSquare className="w-4 h-4 fill-white" /> Claim Combo on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
